import { CycleUtils } from '@wix/package-picker-shared';
import {
  useCycleAvailability,
  useCycleName,
  useLocalization,
} from '@wix/package-picker-shared-studio';
import { PurchaseOffering } from '@wix/premium-purchase-platform/@sdk';
import { StudioPackagePickerModule } from '../../modules/studio-package-picker.module';

interface UseCycleSelectorItemTooltipProps {
  cycle?: PurchaseOffering.PurchaseCycle;
  productId: string;
}

export const useCycleSelectorItemTooltip = ({
  cycle,
  productId,
}: UseCycleSelectorItemTooltipProps) => {
  const { shouldShowJapaneseExperience } = useLocalization();
  const { t } = StudioPackagePickerModule.internals.useTranslations();
  const currentProductQuery =
    StudioPackagePickerModule.SDK.switchApiClient.hooks.useGetCurrentProduct(
      {},
    );
  const isCurrentProduct =
    currentProductQuery.data?.currentProduct?.productId === productId;
  const currentCycle = currentProductQuery.data?.currentProduct?.cycle;

  const { data: product, isLoading: isProductLoading } =
    StudioPackagePickerModule.SDK.studioOfferingApiClient.hooks.useFetchProduct(
      { productId },
    );

  const { isCycleAvailable } = useCycleAvailability(
    productId,
    cycle || undefined,
  );

  const currentCycleName = useCycleName({
    cycle: currentCycle,
    showInMonths: shouldShowJapaneseExperience,
  });

  if (!cycle || currentProductQuery.isLoading || isProductLoading) {
    return null;
  }

  const getTooltipKey = () => {
    if (isCycleAvailable || !currentCycleName) {
      return null;
    }

    const maxPossibleCycle = CycleUtils.maxCycle(
      product?.pricingDetails?.map((p) => p.cycle) ?? [],
    );

    // TODO: Is it still needed? What's the case?
    if (CycleUtils.isCycleEqual(currentCycle, maxPossibleCycle)) {
      return 'Advanced_plans.second_upgrade.cycle.upgrade_max_cycle.disabled_tooltip';
    }

    if (isCurrentProduct) {
      return 'Advanced_plans.second_upgrade.cycle.extend.disabled_tooltip';
    } else {
      return 'Advanced_plans.second_upgrade.cycle.upgrade.disabled_tooltip';
    }
  };
  const tooltipKey = getTooltipKey();
  const tooltipContent = t(tooltipKey ?? '', {
    currentCycle: currentCycleName,
  });

  const onShowTooltip = () => {
    StudioPackagePickerModule.internals.bi.onCycleSelectorTooltipShow?.({
      cycle,
      key: tooltipKey,
    });
  };

  return {
    tooltipKey,
    tooltipContent,
    onShowTooltip,
  };
};
